import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getFeatureDisplayName, getRequiredFeatureForPath } from '@/lib/utils/licenseConfig';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

interface FeatureNotAvailablePageProps {
  featureName?: string;
}

const FeatureNotAvailablePage: React.FC<FeatureNotAvailablePageProps> = ({ featureName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompany } = useSimplifiedUserData();
  
  // Get feature name from props, location state, or current path
  const feature = featureName || 
    (location.state as any)?.featureName || 
    getRequiredFeatureForPath(location.pathname);
  
  const displayName = feature ? getFeatureDisplayName(feature) : 'This Feature';
  const subscriptionName = selectedCompany?.license?.name || selectedCompany?.subscription || 'Current Plan';

  return (
    <div className="flex items-center justify-center min-h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
              <div className="relative bg-primary/10 p-6 rounded-full">
                <Lock className="h-16 w-16 text-primary" />
              </div>
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-center">
            Feature Not Available
          </CardTitle>
          <CardDescription className="text-lg mt-2 text-center">
            {displayName} is not included in your subscription
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6 space-y-3">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg mb-1 text-center">
                  Upgrade Your Plan
                </h3>
                <p className="text-sm text-muted-foreground text-center">
                  To access <span className="font-medium text-foreground">{displayName}</span>, 
                  you'll need to upgrade from your current <span className="font-medium text-foreground">{subscriptionName}</span> plan.
                </p>
              </div>
            </div>
            
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-muted-foreground text-center">
                Contact your company owner or administrator to upgrade your subscription and unlock this feature.
              </p>
            </div>
          </div>

          <div className="flex gap-3 justify-center pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/home')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
            
            <Button
              onClick={() => navigate('/settings')}
              className="gap-2"
            >
              <Sparkles className="h-4 w-4" />
              View Subscription
            </Button>
          </div>
          
          {selectedCompany && (
            <div className="text-xs text-center text-muted-foreground pt-2 border-t">
              Company: <span className="font-medium">{selectedCompany.name}</span>
              {selectedCompany.license && (
                <span className="ml-2">
                  • License: <span className="font-medium">{selectedCompany.license.name}</span>
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureNotAvailablePage;

